import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default, { PureComponent, createRef } from 'react';
import { Transition } from 'react-transition-group';
import PT from 'prop-types';
import { I as InlinePortalProvider } from '../Portal/InlinePortalProvider-CZof7mQs.js';
import { C as Container, O as OuterWrapper, W as Wrapper, H as Header, a as Content, F as Footer } from './Modal.styles-4mJL8nEe.js';
import { C as CloseButton, a as CloseIcon } from './CloseButton-COOUYdvi.js';
import { g as getElementFromRef } from '../LocalModules/storm-ui-utils/dist/esm/getElementFromRef-DUWqD2Fg-DUWqD2Fg.js';
import { t as trapTabFocus } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/trapTabFocus-vpg0lK2r-Bk1xQ7S4.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-BjFtn5aB-BjFtn5aB.js';
import { P as PreventScroll } from '../LocalModules/storm-ui-utils/dist/esm/PreventScroll-xF1A993L-CbrV58O0.js';
import { T as Text } from '../Text/Text-C6JmRCZS.js';
import { T as TaktIdConsumer, a as TaktIdProvider } from '../TaktIdContext/TaktIdContext-DlOuPWuU.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-DaCk9T1Y.js';
import { t as times } from '../LocalModules/storm-ui-icons/dist/esm/localIcons-sBnDaVj5-23EprBeB.js';
import { P as Portal } from '../Portal/Portal-DPcnZ8IV.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

const fadeDuration = 50;
class Modal extends PureComponent {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "closeRef", /*#__PURE__*/createRef());
    _defineProperty(this, "wrapperRef", /*#__PURE__*/createRef());
    _defineProperty(this, "modalContainerRef", /*#__PURE__*/createRef());
    _defineProperty(this, "teardownModal", () => {
      // Bring focus back to the toggle element
      if (this.props.toggleEl) {
        const element = getElementFromRef(this.props.toggleEl);
        if (element !== null && element !== void 0 && element.focus) {
          element.focus();
        }
      }
    });
    _defineProperty(this, "handleKeyUp", event => {
      const {
        onClose
      } = this.props;
      if (event.key === 'Escape') {
        event.preventDefault();
        if (onClose) {
          onClose();
        }
      }
    });
    _defineProperty(this, "handleKeyDown", event => {
      if (this.modalContainerRef.current) {
        trapTabFocus(event, this.modalContainerRef.current);
      }
    });
    _defineProperty(this, "handleClick", event => {
      const {
        target,
        currentTarget
      } = event;
      const {
        onClose
      } = this.props;
      if (target === currentTarget) {
        event.preventDefault();
        if (onClose) {
          onClose();
        }
      }
    });
    _defineProperty(this, "handleEntered", () => {
      const focusableEls = getFocusableElements(this.wrapperRef.current);
      if (focusableEls.length > 0) {
        focusableEls[0].focus();
      }
    });
  }
  componentWillUnmount() {
    const {
      isOpen
    } = this.props;
    // Run `teardownModal()` if the Modal was open at unmount time.
    if (isOpen) {
      this.teardownModal();
    }
  }
  renderContents(state) {
    const {
      children,
      closeButtonProps,
      header,
      footer,
      hasCloseButton,
      contentClassName,
      onClose,
      padding,
      closeButtonLabel = 'Close modal',
      /* omitting from rest */
      taktId,
      taktValue,
      isOpen,
      modalElementId,
      ...rest
    } = this.props;
    return /*#__PURE__*/React__default.createElement(Container, _extends({}, rest, {
      onMouseDown: this.handleClick,
      onKeyUp: this.handleKeyUp,
      onKeyDown: this.handleKeyDown,
      role: "presentation",
      ref: this.modalContainerRef,
      $fadeDuration: fadeDuration,
      $transitionState: state
    }), /*#__PURE__*/React__default.createElement(PreventScroll, null, /*#__PURE__*/React__default.createElement(OuterWrapper, {
      $transitionState: state,
      ref: this.wrapperRef
    }, /*#__PURE__*/React__default.createElement(InlinePortalProvider, null, /*#__PURE__*/React__default.createElement(Wrapper, null, (header || hasCloseButton) && /*#__PURE__*/React__default.createElement(Header, null, /*#__PURE__*/React__default.createElement(Text, {
      type: "h1",
      styleAs: "h4"
    }, header), hasCloseButton && /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
      taktId: closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.taktId,
      taktValue: taktValue,
      fallbackId: createStormTaktId('close-button')
    }, ({
      getDataTaktAttributes
    }) => /*#__PURE__*/React__default.createElement(CloseButton, _extends({
      closeButtonLabel: closeButtonLabel,
      onClick: onClose,
      ref: this.closeRef
    }, getDataTaktAttributes({
      taktValue: closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.taktValue
    }), closeButtonProps), /*#__PURE__*/React__default.createElement(CloseIcon, {
      type: times
    })))), /*#__PURE__*/React__default.createElement(Content, {
      className: contentClassName,
      $padding: padding
    }, children), footer && /*#__PURE__*/React__default.createElement(Footer, null, footer))))));
  }
  render() {
    const {
      isOpen,
      modalElementId,
      taktId,
      taktValue
    } = this.props;
    return /*#__PURE__*/React__default.createElement(Transition, {
      timeout: fadeDuration,
      appear: true,
      in: isOpen,
      onExit: this.teardownModal,
      onEntered: this.handleEntered,
      nodeRef: this.modalContainerRef
    }, state => {
      if (state === 'exited') {
        return null;
      }
      return /*#__PURE__*/React__default.createElement(Portal, {
        containerId: modalElementId
      }, /*#__PURE__*/React__default.createElement(TaktIdProvider, {
        taktId: taktId,
        taktValue: taktValue,
        fallbackId: createStormTaktId('modal')
      }, this.renderContents(state)));
    });
  }
}
_defineProperty(Modal, "propTypes", {
  /**
   * Control open / close of modal. It will animate automatically on change.
   */
  isOpen: PT.bool,
  /**
   * The React nodes/nodes that are rendered as content of the Modal.
   */
  children: PT.oneOfType([PT.arrayOf(PT.node), PT.node]).isRequired,
  /**
   * No header will be rendered if this is blank, UNLESS hasCloseButton is true.
   */
  header: PT.oneOfType([PT.arrayOf(PT.node), PT.node]),
  /**
   * Most modals will have a primary and secondary action here, such as "save" and "cancel".
   */
  footer: PT.oneOfType([PT.arrayOf(PT.node), PT.node, PT.any]),
  /**
   * Controls display of the close button.
   */
  hasCloseButton: PT.bool,
  /**
   * Customized text label for close button.
   */
  closeButtonLabel: PT.string,
  /**
   * Props that are applied to the CloseButton component.
   * if `label` is set, it will override closeButtonLabel prop
   */
  closeButtonProps: PT.objectOf(PT.any),
  /**
   * Fired when any close action occurs, such as: clicking outside, hitting close button,
   * or hitting escape.
   */
  onClose: PT.func,
  /**
   * Sets the padding around the modal's content.
   */
  padding: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  /**
   * Use if you need to add a class name to the inner content div.
   */
  contentClassName: PT.string,
  /**
   * This should correspond to the ROOT level dom node id that the modal will render into.
   * When this prop is not explicitly passed a value, the modal will mount into the div with
   * id matching the portalElementId property in the popover object of the theme.
   */
  modalElementId: PT.string,
  /**
   * Pass a ref to an element here to use as the toggle element.
   */
  toggleEl: PT.oneOfType([PT.instanceOf(typeof Element !== 'undefined' ? Element : Object), PT.shape({
    current: PT.instanceOf(typeof Element !== 'undefined' ? Element : Object)
  })])
});
_defineProperty(Modal, "defaultProps", {
  closeButtonLabel: 'Close modal',
  closeButtonProps: undefined,
  contentClassName: '',
  footer: undefined,
  hasCloseButton: true,
  header: undefined,
  isOpen: false,
  modalElementId: undefined,
  onClose: noop,
  padding: undefined,
  toggleEl: undefined
});

export { Modal as M };
//# sourceMappingURL=Modal-Ck7RSkHa.js.map
